import { useEffect } from "react";
import { XCircle } from "lucide-react";
import "./ScanFlashOverlay.css";

interface ScanFlashOverlayProps {
    /** True while the bad-scan feedback should be visible. */
    active: boolean;
    /** Optional operator guidance for failures where a bare X is insufficient. */
    message?: string;
}

/**
 * Full-viewport bad-scan feedback, shared by every picking workflow
 * (Single / Pick & Sort / Large / Shelf Drop / Forecast Drop).
 *
 * Renders a translucent red wash plus a centred X as a fixed overlay
 * ABOVE all page chrome (pointer-events: none, so scans and taps pass
 * straight through). The previous per-screen approach tinted the page
 * container's background, which the unified PickingScreen's opaque
 * cards and action bar painted over — the picker barely saw the red.
 * An overlay reads identically on every layout.
 *
 * Always mounted; visibility toggles via the `--active` class so the
 * first error doesn't pay a mount/layout cost mid-scan.
 */
const ScanFlashOverlay = ({ active, message }: ScanFlashOverlayProps) => {
    // Short haptic buzz alongside the visual + audio error cues — the
    // Zebra handhelds (Android Chrome) support navigator.vibrate; a
    // picker mid-reach feels the rejection without looking up.
    // Progressive enhancement: silently a no-op where unsupported.
    useEffect(() => {
        if (active) navigator.vibrate?.(80);
    }, [active]);

    return (
        <div
            className={"scan-flash-overlay" + (active ? " scan-flash-overlay--active" : "")}
            role={message ? "alert" : undefined}
            aria-hidden={message ? undefined : true}
        >
            <XCircle className="scan-flash-overlay-icon" strokeWidth={2} />
            {message && <div className="scan-flash-overlay-message">{message}</div>}
        </div>
    );
};

export default ScanFlashOverlay;
